import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { Alert, Button, Grid, Snackbar } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { useAppContextController } from "context/AppContext";
import { EventContextProvider } from "context/EventContext";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import useCompanyCache from "hooks/useCompanyCache";
import useGetBackButton from "hooks/useGetBackButton";
import useHandleBackButtonMainPage from "hooks/useHandleBackButtonMainPage";
import useVenueCache from "hooks/useVenueCache";
import moment from "moment";
import { useSnackbar } from "notistack";
import { useQueryClient } from "react-query";
import generateFiltersFromString from "utils/generateFiltersFromString";
import { getPeriodForDate } from "utils/helpers/payrollHelper";
import InvoiceEditorTable from "../payroll/components/InvoiceEditorTable";
import InvoicesFilterCards from "./components/InvoicesFilterCards";
import InvoicesTable from "./components/InvoicesTable";

function Invoices() {
  const {
    currentLoggedUser,
    venues,
    setVenues,
    userType,
    isMobile,
    currentApplicant,
    company,
    setCompany,
    setCompanyType,
    currentInvoice,
    setCurrentInvoice,
  } = useAppContextController();
  const { isLoadingCompany, refetchCompany } = useCompanyCache({
    company,
    setCompany,
    setCompanyType,
  });
  const { isLoadingVenues, refetchVenues } = useVenueCache({ venues, setVenues, company });
  const { enqueueSnackbar } = useSnackbar();

  const startOfPeriodDay = company?.payrollSettings?.payrollStartOfPeriod ?? 1;
  const periodLength = company?.payrollSettings?.payrollPeriodLength || "weekly";
  const shiftPeriodWeek = (company?.payrollSettings?.payrollShiftPeriodWeek || "No") === "Yes";

  const initialPeriod = useMemo(() => {
    const todayMoment = moment();
    return getPeriodForDate(
      todayMoment.toISOString(),
      periodLength,
      shiftPeriodWeek,
      startOfPeriodDay
    );
  }, [periodLength, shiftPeriodWeek, startOfPeriodDay]);

  const [currentPeriod, setCurrentPeriod] = useState(initialPeriod);

  const [searchParams] = useSearchParams();
  const myVenues = searchParams.get("myVenues");
  const entityType = searchParams.get("entityType");
  const panel = searchParams.get("panel");
  const venueSlug = searchParams.get("venueSlug");
  const companySlug = searchParams.get("companySlug");
  const eventUrl = searchParams.get("eventUrl");
  const jobSlug = searchParams.get("jobSlug");

  // Handle back button for this screen
  const {
    backListUrl,
    filterParam,
    pageParam,
    subFilterParam,
    subPageParam,
    pageFromUrl,
    filtersFromUrl,
    subPageFromUrl,
    subFiltersFromUrl,
    navigateToListWithBack,
    navigateToScreenWithBack,
  } = useHandleBackButtonMainPage({
    baseUrl: "/invoices",
    screenName: "Invoices",
  });

  // Get back button generated by other screens
  const { backParam, backToTitle, backToUrl, backButton } = useGetBackButton();

  const eventListNavigateFuncRef = useRef(null);
  const bindEventListNavigateFunc = (func) => {
    eventListNavigateFuncRef.current = func;
  };

  const queryClient = useQueryClient();
  const isEdit = !!currentInvoice?._id;

  const { action, invoiceId } = useParams();
  const [open, setOpen] = useState(false);
  const [eventPreview, setEventPreview] = useState(null);
  const [jobPreview, setJobPreview] = useState(null);

  const [filters, setFilters] = useState({
    entityType: entityType ?? (company?.companyType === "Venue" ? "Event" : "Job"),
  });
  const [actionSelected, setActionSelected] = useState(action || "");
  const [exportFieldModalOpen, setExportFieldModalOpen] = useState(false);
  const [exportRosterStatus, setExportRosterStatus] = useState(null);
  const [isMessageModalOpen, toggleMessageModal] = useState(false);
  const [applicantsData, setApplicantsData] = useState([]);
  const [rosterData, setRosterData] = useState([]);
  const [partnerRosterData, setPartnerRosterData] = useState([]);
  const [previewContent, setPreviewContent] = useState("all");
  const [notesModalOpen, setNoteModalOpen] = useState(false);
  const [toastAlert, setToastAlert] = useState({
    isShow: false,
    message: "",
  });

  const navigate = useNavigate();

  useEffect(() => {
    // this check added as default added when company loaded
    if (isLoadingCompany) return;

    let newFilters = { ...filters };
    if (venueSlug) {
      newFilters = {
        ...newFilters,
        venueSlug,
      };
    }
    if (companySlug) {
      newFilters = {
        ...newFilters,
        companySlug,
      };
    }
    if (eventUrl) {
      newFilters = {
        ...newFilters,
        eventUrl,
      };
    }
    if (jobSlug) {
      newFilters = {
        ...newFilters,
        jobSlug,
      };
    }
    const tempEntityType = filters?.entityType || entityType;
    if (tempEntityType) {
      newFilters = {
        ...newFilters,
        entityType: tempEntityType,
      };
    }
    if (newFilters?.eventUrl && filters?.entityType === "Job") {
      newFilters = { ...newFilters, entityType: "Event" };
    }
    if (newFilters?.jobSlug && filters?.entityType === "Event") {
      newFilters = { ...newFilters, entityType: "Job" };
    }
    setFilters(newFilters);
    if (action) {
      setActionSelected(action);
    } else {
      setActionSelected("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, entityType, eventUrl, jobSlug, isLoadingCompany, venueSlug, companySlug]);

  useEffect(() => {
    if (filterParam != null) {
      if (filterParam) {
        const newFilters = generateFiltersFromString(filterParam);
        setFilters(newFilters);
      } else {
        setFilters({});
      }
    }
  }, [filterParam]);

  const setNavigateUrl = (url) => navigate(url);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Snackbar
        open={toastAlert.isShow}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setToastAlert({ isShow: false, message: "", status: "" })}
        key="top-center"
      >
        <Alert
          onClose={() => setToastAlert({ isShow: false, message: "", status: "" })}
          // severity={toastAlert.status}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toastAlert.message}
        </Alert>
      </Snackbar>
      <EventContextProvider>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12}>
            {backToUrl ? (
              <Grid item xs={12} sm={12} md={12} pb={5} pt={3}>
                {backButton}
              </Grid>
            ) : (
              <>
                {company?.companyType === "Venue" && (
                  <InvoicesFilterCards
                    filters={filters}
                    setFilters={setFilters}
                    currentPeriod={currentPeriod}
                  />
                )}
              </>
            )}
            <InvoicesTable
              setActionSelected={setActionSelected}
              filters={filters}
              title="Invoices"
              setFilters={setFilters}
              setNavigateUrl={setNavigateUrl}
              defaultSort="eventDate"
              // setOpen={setOpen}
              parent="Invoices"
              // setOpen={toggleEventPreviewModal}
              setEventPreview={setEventPreview}
              setJobPreview={setJobPreview}
              setPreviewContent={setPreviewContent}
              // bindRefetchEvents={bindRefetchEvents}
              setToastAlert={setToastAlert}
              parentPage={pageFromUrl}
              parentNavigateWithBack={navigateToListWithBack}
              bindNavigateFunc={bindEventListNavigateFunc}
              currentPeriod={currentPeriod}
              setCurrentPeriod={setCurrentPeriod}
            />
          </Grid>
          {backListUrl && (
            <Grid item xs={12} sm={12} md={12} mt={2}>
              <Button
                variant="contained"
                startIcon={<ArrowBackIcon />}
                onClick={() => {
                  navigate(backListUrl);
                }}
              >
                Back to Invoices List
              </Button>
            </Grid>
          )}
          {["invoiceinfo", "invoiceall", ""].includes(actionSelected) && !!currentInvoice?._id && (
            <>
              <Grid item xs={12} sm={12} md={12}>
                <InvoiceEditorTable
                  title={`Invoice Editor for: ${
                    currentInvoice?.eventName || currentInvoice?.jobName
                  }`}
                  navigateToAction="employeeinfo"
                  pageSize={50}
                  parentPage={subPageFromUrl}
                  parentFilters={subFiltersFromUrl}
                  parentNavigateWithBack={navigateToScreenWithBack}
                  currentPeriod={currentPeriod}
                  fullscreen
                  panelOptions={[]}
                  burdenRate={{}}
                  isLoadingBurdenRate={false}
                  parentInvoiceId={currentInvoice?._id}
                  parentEventUrl={currentInvoice?.eventUrl}
                  parentJobSlug={currentInvoice?.jobSlug}
                />
              </Grid>
            </>
          )}
        </Grid>
      </EventContextProvider>
    </DashboardLayout>
  );
}

// Setting default values for the props of BaseLayout
// Events.defaultProps = {
//   pageName: "Events",
// };

// // Typechecking props for BaseLayout
// Events.propTypes = {
//   pageName: PropTypes.string,
// };

export default Invoices;
